import * as React from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { Check, Minus } from "lucide-react";
import { cn } from "../../lib/utils.js";

/**
 * La casilla venía de shadcn con tres clases que este design system nunca tuvo
 * —`text-primary-foreground`, `ring-offset-background`, `ring-ring`— y que por
 * eso no compilaban a NINGUNA regla (comprobado sobre el CSS de `vite build`:
 * no existen `--color-primary-foreground`, `--color-background` ni
 * `--color-ring`). No eran ruido inofensivo:
 *
 *   · El glifo de la casilla marcada se quedaba sin color propio y heredaba el
 *     del contexto, que puede ser cualquiera — en una fila de tabla, el gris de
 *     `text-text-muted` sobre el azul del relleno.
 *   · `ring-offset-2` SÍ existe (es espaciado), pero sin color de offset
 *     Tailwind cae a su default, que es blanco. En tema oscuro el foco dibujaba
 *     un hueco blanco alrededor de la casilla.
 *
 * El par correcto encima de `bg-primary-vivid` es el blanco: 3.98:1 en oscuro y
 * 5.48:1 en claro. El glifo es un objeto gráfico, así que el mínimo que le
 * aplica es el 3:1 de WCAG 1.4.11 —no el 4.5:1 de texto— y lo cumple en los dos
 * temas. El anillo de foco es el mismo del resto del sistema (`Button`,
 * `Switch`, `Fab`): `ring-primary/50` y offset 0.
 *
 * El borde en reposo es `border-strong`, el rol que `tailwind.config.js`
 * reserva para IDENTIFICAR un control de formulario, y el mismo que usa
 * `Input`. Venía en `border-primary` —el default de shadcn— y eso NO era un
 * problema de contraste: el azul cumple 1.4.11 sobre las cinco superficies
 * donde se dibuja (3.67:1 el peor caso, en oscuro sobre `surface-1`). Era un
 * problema de significado. Una casilla VACÍA pintada del color de marca dice
 * "esto está activo" sin estarlo, y encima desafinaba con la casilla nativa de
 * `FeatureCheckboxGrid`, que en reposo usa el gris del navegador y sólo pinta
 * de marca al marcarse (`accent-primary`). Ahora las dos cuentan lo mismo:
 * gris cuando está vacía, azul cuando está marcada.
 *
 * Al marcarse el borde se vuelve `transparent` y el relleno lo pinta por debajo
 * (`background-clip` es `border-box` por defecto), así que la caja se lee como
 * un cuadrado sólido en vez de un azul con anillo gris. Es lo mismo que hace
 * `Switch` con su vía encendida.
 *
 * OJO con la alternativa obvia: `border-primary-vivid` NO EXISTE. `vivid` se
 * declaró en `theme.extend.backgroundColor`, no en `colors`, así que sólo hay
 * `bg-primary-vivid` — la versión de borde compila a nada. El valor equivalente
 * ahí es `border-primary` a secas, porque `colors.primary.DEFAULT` y
 * `backgroundColor.primary.vivid` apuntan los dos a `--color-primary`. Con
 * `transparent` no hay que acordarse de eso, y si el relleno cambia de token el
 * borde lo sigue solo.
 *
 * No lleva el `focus-visible:border-primary` de `Input`: en 16px, bajo un
 * anillo de 2px, cambiar el borde no se ve.
 */
const Checkbox = React.forwardRef(({ className, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(
      [
        "peer h-4 w-4 shrink-0 rounded-sm border border-border-strong",
        "focus-visible:outline-none",
        "focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-0",
        "disabled:cursor-not-allowed disabled:opacity-50",
        "data-[state=checked]:border-transparent data-[state=checked]:bg-primary-vivid data-[state=checked]:text-white",
        "data-[state=indeterminate]:border-transparent data-[state=indeterminate]:bg-primary-vivid data-[state=indeterminate]:text-white",
      ].join(" "),
      className,
    )}
    {...props}
  >
    <CheckboxPrimitive.Indicator
      className={cn("flex items-center justify-center text-current")}
    >
      {props.checked === "indeterminate" ? (
        <Minus className="h-3 w-3" />
      ) : (
        <Check className="h-4 w-4" />
      )}
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
));
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };
